import { useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BOOKING_MODES, WEEKDAY_LABELS } from "../constants";
import {
  ADMIN_MAX_DATE_RANGE_DAYS,
  getAdminMaxRangeEndDate,
  formatAdminDisplayDate,
  generateDateRange,
  generateRecurringDates,
  getAdminMaxSelectableDate,
  getLocalISODate,
} from "../utils";
import { InputLabel } from "./Shared";

const BookingForm = ({
  selectedSection,
  users = [],
  bookingForm,
  setBookingForm,
  previewDateCount,
  handleCreateBooking,
  handleRemoveDate,
  handleBookingDateChange,
  handleToggleWeekday,
  isAdmin = true,
  sectionBookings = [],
  workspaceBookings = [],
  selectedSeatId = null,
  selectedSeatDetails = null,
}) => {
  const seatHasPermanentAssignment = useMemo(
    () =>
      Boolean(
        selectedSeatDetails?.permanent_booking ||
          selectedSeatDetails?.bookings?.some(
            (b) => (b.booking_type || b.bookingType || "").toLowerCase() === "permanent"
          )
      ),
    [selectedSeatDetails]
  );

  const filteredBookingModes = isAdmin
    ? BOOKING_MODES
    : BOOKING_MODES.filter((mode) => mode.value !== "permanent");

  const minBookingDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const maxBookingDate = useMemo(() => getAdminMaxSelectableDate(minBookingDate), [minBookingDate]);

  const minEndDate = useMemo(() => {
    if (!bookingForm.startDate) return minBookingDate;

    const startDate = new Date(`${bookingForm.startDate}T00:00:00`);
    return startDate > minBookingDate ? startDate : minBookingDate;
  }, [bookingForm.startDate, minBookingDate]);

  const previousRecurringStateRef = useRef({
    bookingMode: bookingForm.bookingMode,
    startDate: bookingForm.startDate,
  });

  useEffect(() => {
    const previous = previousRecurringStateRef.current;
    const enteredRecurring =
      previous.bookingMode !== "recurring" && bookingForm.bookingMode === "recurring";
    const recurringStartChanged =
      previous.bookingMode === "recurring" &&
      bookingForm.bookingMode === "recurring" &&
      previous.startDate !== bookingForm.startDate;

    if ((enteredRecurring || recurringStartChanged) && bookingForm.startDate) {
      const nextDefaultEndDate = getLocalISODate(
        getAdminMaxRangeEndDate(new Date(`${bookingForm.startDate}T00:00:00`)),
      );

      setBookingForm((current) => ({
        ...current,
        endDate: nextDefaultEndDate,
      }));
    }

    previousRecurringStateRef.current = {
      bookingMode: bookingForm.bookingMode,
      startDate: bookingForm.startDate,
    };
  }, [bookingForm.bookingMode, bookingForm.startDate, setBookingForm]);

  // Dates already booked for this user across any section (global check)
  const userBookedDates = useMemo(() => {
    if (!bookingForm.bookedFor) return new Set();
    const dates = new Set();
    
    // Check all workspace bookings
    workspaceBookings.forEach((b) => {
      if (b.bookedFor === bookingForm.bookedFor && b.status === "active") {
        if (b.date && b.date !== "permanent") dates.add(b.date);
      }
    });

    // Check current section bookings
    sectionBookings.forEach((b) => {
      if (b.bookedFor === bookingForm.bookedFor && b.status === "active") {
        if (b.date && b.date !== "permanent") dates.add(b.date);
      }
    });
    
    return dates;
  }, [bookingForm.bookedFor, workspaceBookings, sectionBookings]);

  // Is user permanently booked anywhere?
  const isUserPermanentlyBooked = useMemo(() => {
    if (!bookingForm.bookedFor) return false;
    return (
      workspaceBookings.some(b => b.bookedFor === bookingForm.bookedFor && b.status === "active" && b.date === "permanent") ||
      sectionBookings.some(b => b.bookedFor === bookingForm.bookedFor && b.status === "active" && b.date === "permanent")
    );
  }, [bookingForm.bookedFor, workspaceBookings, sectionBookings]);

  // Dates already booked for this seat specifically
  const bookedDates = useMemo(() => {
    if (!selectedSeatId) return new Set();
    return new Set(
      sectionBookings
        .filter(
          (b) =>
            b.seatId === selectedSeatId &&
            b.status === "active" &&
            b.date &&
            b.date !== "permanent"
        )
        .map((b) => b.date)
    );
  }, [sectionBookings, selectedSeatId]);

  const hasPermanentBooking = useMemo(
    () =>
      selectedSeatId &&
      sectionBookings.some(
        (b) =>
          b.seatId === selectedSeatId &&
          b.status === "active" &&
          b.date === "permanent"
      ),
    [sectionBookings, selectedSeatId]
  );

  const hasPreviousBookings = useMemo(() => {
    if (!selectedSeatId) return false;

    if (selectedSeatDetails?.bookings && Array.isArray(selectedSeatDetails.bookings)) {
      const hasNonPermanent = selectedSeatDetails.bookings.some(
        (b) => {
          const type = (b.booking_type || b.bookingType || "").toLowerCase();
          return type && type !== "permanent";
        }
      );
      if (hasNonPermanent) return true;
    }

    return sectionBookings.some(
      (b) =>
        b.seatId === selectedSeatId &&
        b.status === "active" &&
        b.date &&
        b.date !== "permanent"
    );
  }, [selectedSeatDetails, sectionBookings, selectedSeatId]);

  // Calculate generated dates for range/recurring to check conflicts
  const generatedDates = useMemo(() => {
    if (bookingForm.bookingMode === "range") {
      if (!bookingForm.startDate || !bookingForm.endDate || new Date(bookingForm.startDate) > new Date(bookingForm.endDate)) return [];
      return generateDateRange(bookingForm.startDate, bookingForm.endDate);
    }
    if (bookingForm.bookingMode === "recurring") {
      if (!bookingForm.startDate || !bookingForm.endDate || new Date(bookingForm.startDate) > new Date(bookingForm.endDate)) return [];
      if (bookingForm.weekdays.length === 0) return [];
      return generateRecurringDates(bookingForm.startDate, bookingForm.endDate, bookingForm.weekdays);
    }
    return [];
  }, [bookingForm.bookingMode, bookingForm.startDate, bookingForm.endDate, bookingForm.weekdays]);

  // Per-date conflict flags (for "specific" mode)
  // Check if seat is booked OR user already has a booking on this date
  const dateConflicts = useMemo(() => {
    if (bookingForm.bookingMode !== "specific") return [];
    return bookingForm.dates.map((d) => {
      if (!d) return null;
      if (bookedDates.has(d)) return "seat";
      if (userBookedDates.has(d)) return "user";
      return null;
    });
  }, [bookingForm.bookingMode, bookingForm.dates, bookedDates, userBookedDates]);

  const generatedDatesConflicts = useMemo(() => {
    if (bookingForm.bookingMode !== "range" && bookingForm.bookingMode !== "recurring") return null;
    for (const d of generatedDates) {
      if (bookedDates.has(d)) return "seat";
      if (userBookedDates.has(d)) return "user";
    }
    return null;
  }, [bookingForm.bookingMode, generatedDates, bookedDates, userBookedDates]);

  const hasAnyConflict =
    dateConflicts.some(Boolean) ||
    generatedDatesConflicts !== null ||
    generatedDates.length > ADMIN_MAX_DATE_RANGE_DAYS ||
    (bookingForm.bookingMode === "permanent" && (hasPermanentBooking || hasPreviousBookings || isUserPermanentlyBooked || userBookedDates.size > 0));

  const isPermanentModeBlocked =
    isAdmin &&
    bookingForm.bookingMode === "permanent" &&
    seatHasPermanentAssignment;
  const isSubmitDisabled =
    isPermanentModeBlocked || hasAnyConflict || (!bookingForm.isBlockedSeat && !bookingForm.bookedFor);

  return (
    <form
      onSubmit={handleCreateBooking}
      className=" border border-slate-200 bg-slate-50 p-8 "
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-medium text-slate-950">Manage Seat</h3>
        {!isAdmin && (
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-indigo-600">
            User Mode
          </span>
        )}
      </div>

      {seatHasPermanentAssignment && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
            Permanent Assignment Active
          </p>
          <p className="mt-1 text-xs font-medium text-amber-700">
            This seat has a permanent assignee. You can still create date-based bookings when no active booking exists for the selected date, but you cannot create another permanent assignment.
          </p>
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 flex items-center gap-4 border-b border-slate-200 pb-4">
          <button
            type="button"
            onClick={() => setBookingForm((prev) => ({ ...prev, isBlockedSeat: false }))}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              !bookingForm.isBlockedSeat
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Book for User
          </button>
          <button
            type="button"
            onClick={() => setBookingForm((prev) => ({ ...prev, isBlockedSeat: true, bookedFor: "" }))}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              bookingForm.isBlockedSeat
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Block Seat
          </button>
        </div>
      )}

      {!bookingForm.isBlockedSeat && (
        <div className="mt-6">
        <InputLabel>{isAdmin ? "Select User" : "Booked For"}</InputLabel>
        {isAdmin ? (
          <Select
            options={users.map((user) => ({
              value: user.id,
              label: user.fullName,
            }))}
            value={
              bookingForm.bookedFor
                ? {
                    value: bookingForm.bookedFor,
                    label:
                      users.find((user) => user.id === bookingForm.bookedFor)
                        ?.fullName || bookingForm.bookedFor,
                  }
                : null
            }
            onChange={(option) =>
              setBookingForm((current) => ({
                ...current,
                bookedFor: option ? option.value : "",
              }))
            }
            isDisabled={false}
            isClearable
            isSearchable
            placeholder="Search and select a person..."
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: "1rem",
                borderColor: "#e2e8f0",
                padding: "6px",
                boxShadow: "none",
                fontSize: "0.875rem",
                fontWeight: "700",
                "&:hover": { borderColor: "#cbd5e1" },
              }),
            }}
          />
        ) : (
          <input
            value={bookingForm.bookedFor}
            onChange={(event) =>
              setBookingForm((current) => ({
                ...current,
                bookedFor: event.target.value,
              }))
            }
            placeholder="Enter name"
            readOnly={!isAdmin}
            disabled={false}
            className={`w-full rounded-2xl border border-slate-200 px-5 py-4 text-sm font-bold outline-none transition focus:ring-2 focus:ring-slate-900/5 ${
              !isAdmin ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"
            }`}
          />
        )}
      </div>
      )}

      <div className="mt-6">
        <InputLabel>Booking Schedule</InputLabel>
        <div className="grid grid-cols-2 gap-3">
          {filteredBookingModes.map((mode) => (
            <button
              key={mode.value}
              type="button"
              disabled={mode.value === "permanent" && seatHasPermanentAssignment}
              onClick={() =>
                setBookingForm((prev) => {
                  if (mode.value !== "recurring") {
                    return { ...prev, bookingMode: mode.value };
                  }

                  const recurringStartDate = prev.startDate || getLocalISODate(minBookingDate);
                  const defaultRecurringEndDate = getLocalISODate(
                    getAdminMaxRangeEndDate(new Date(`${recurringStartDate}T00:00:00`)),
                  );

                  return {
                    ...prev,
                    bookingMode: mode.value,
                    startDate: recurringStartDate,
                    endDate: defaultRecurringEndDate,
                  };
                })
              }
              className={`rounded-2xl border-2 px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                bookingForm.bookingMode === mode.value
                  ? "border-slate-900 bg-slate-900 text-white shadow-md"
                  : "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600"
              } ${mode.value === "permanent" && seatHasPermanentAssignment ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {bookingForm.bookingMode === "specific" && (
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <InputLabel>Dates</InputLabel>
          </div>
          <div className="space-y-3">
            {bookingForm.dates.map((date, index) => {
              const isConflict = dateConflicts[index];
              return (
                <div key={index}>
                  <div className="group flex items-center gap-3">
                    <div className="flex-1 admin-custom-date-picker">
                      <DatePicker
                        selected={date ? new Date(`${date}T00:00:00`) : null}
                        minDate={minBookingDate}
                        maxDate={maxBookingDate}
                        disabled={false}
                        onChange={(d) => {
                          if (d) {
                            handleBookingDateChange(
                              { target: { value: getLocalISODate(d) } },
                              index
                            );
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        filterDate={(d) => d.getDay() !== 0 && d.getDay() !== 6}
                        customInput={
                          <input
                            disabled={false}
                            className={`w-full rounded-2xl border px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900/5 ${
                              isConflict
                                ? "border-rose-300 bg-rose-50 focus:ring-rose-200"
                                : "border-slate-200 bg-white"
                            }`}
                          />
                        }
                      />
                    </div>
                    {bookingForm.dates.length > 1 && (
                      <button
                        type="button"
                        disabled={false}
                        onClick={() => handleRemoveDate(index)}
                        className="rounded-xl bg-white border border-slate-200 p-3 text-slate-400 transition hover:bg-rose-50 hover:text-rose-500"
                      >
                        <span className="block h-4 w-4">✕</span>
                      </button>
                    )}
                  </div>
                  {isConflict === "seat" && (
                    <p className="mt-1.5 ml-1 text-xs font-semibold text-rose-500">
                      Reservation already exists for this seat
                    </p>
                  )}
                  {isConflict === "user" && (
                    <p className="mt-1.5 ml-1 text-xs font-semibold text-rose-500">
                      User already has a seat booked in another section for this date.
                    </p>
                  )}
                  {isConflict === "user_permanent" && (
                    <p className="mt-1.5 ml-1 text-xs font-semibold text-rose-500">
                      User has a permanent seat assignment.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {bookingForm.bookingMode === "range" && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="admin-custom-date-picker">
              <InputLabel>Starts On</InputLabel>
              <DatePicker
                selected={bookingForm.startDate ? new Date(`${bookingForm.startDate}T00:00:00`) : null}
                minDate={minBookingDate}
                maxDate={maxBookingDate}
                disabled={false}
                onChange={(d) => {
                  if (d) {
                    setBookingForm((prev) => ({
                      ...prev,
                      startDate: getLocalISODate(d),
                    }));
                  }
                }}
                filterDate={(d) => d.getDay() !== 0 && d.getDay() !== 6}
                dateFormat="dd-MM-yyyy"
                customInput={
                  <input
                    disabled={false}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900/5"
                  />
                }
              />
            </div>
            <div className="admin-custom-date-picker">
              <InputLabel>Ends On</InputLabel>
              <DatePicker
                selected={bookingForm.endDate ? new Date(`${bookingForm.endDate}T00:00:00`) : null}
                minDate={minEndDate}
                maxDate={maxBookingDate}
                disabled={false}
                onChange={(d) => {
                  if (d) {
                    setBookingForm((prev) => ({ ...prev, endDate: getLocalISODate(d) }));
                  }
                }}
                filterDate={(d) => d.getDay() !== 0 && d.getDay() !== 6}
                dateFormat="dd-MM-yyyy"
                customInput={
                  <input
                    disabled={false}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900/5"
                  />
                }
              />
            </div>
          </div>
        </div>
      )}

      {bookingForm.bookingMode === "recurring" && (
        <div className="mt-6 space-y-4">
          <div className="admin-custom-date-picker">
            <InputLabel>Starts On</InputLabel>
            <DatePicker
              selected={bookingForm.startDate ? new Date(`${bookingForm.startDate}T00:00:00`) : null}
              minDate={minBookingDate}
              maxDate={maxBookingDate}
              disabled={false}
              onChange={(d) => {
                if (d) {
                  setBookingForm((prev) => ({
                    ...prev,
                    startDate: getLocalISODate(d),
                    endDate: getLocalISODate(getAdminMaxRangeEndDate(d)),
                  }));
                }
              }}
              filterDate={(d) => d.getDay() !== 0 && d.getDay() !== 6}
              dateFormat="dd-MM-yyyy"
              customInput={
                <input
                  disabled={false}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900/5"
                />
              }
            />
          </div>

          <div>
            <InputLabel>Repeat On</InputLabel>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_LABELS.map((label, day) => (
                <button
                  key={day}
                  type="button"
                  disabled={false}
                  onClick={() => handleToggleWeekday(day)}
                  className={`rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-tighter transition-all ${
                    bookingForm.weekdays.includes(day)
                      ? "bg-slate-900 text-white shadow-md scale-110"
                      : "bg-white border border-slate-200 text-slate-400 hover:border-slate-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {previewDateCount !== null && previewDateCount > 0 && !generatedDatesConflicts && (
            <div className="flex items-center gap-3 rounded-2xl bg-indigo-50 px-5 py-4 text-xs font-bold text-indigo-700 border border-indigo-100">
              <div className="h-2 w-2 rounded-full bg-indigo-600 animate-pulse" />
              <span>
                {previewDateCount} booking{previewDateCount !== 1 ? "s" : ""} will be reserved.
              </span>
            </div>
          )}
          {generatedDatesConflicts === "seat" && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                Reservation already exists for this seat on one or more selected dates.
              </p>
            </div>
          )}
          {generatedDatesConflicts === "user" && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                User already has a seat booked in another section for one or more selected dates.
              </p>
            </div>
          )}
          {generatedDatesConflicts === "user_permanent" && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                User has a permanent seat assignment.
              </p>
            </div>
          )}
          {bookingForm.bookingMode === "recurring" && (
            <div className="mt-4 rounded-2xl border border-indigo-100 bg-white/80 p-4">
              <p className="text-xs font-medium text-indigo-700">
                Recurring bookings default to 60 days from the selected start date. The end date is set automatically.
              </p>
            </div>
          )}
          {generatedDates.length > ADMIN_MAX_DATE_RANGE_DAYS && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                Admin bookings can span at most {ADMIN_MAX_DATE_RANGE_DAYS} days.
              </p>
            </div>
          )}
        </div>
      )}

      {bookingForm.bookingMode === "permanent" && isAdmin && (
        <div className="mt-6 rounded-[24px] border-2 border-indigo-100 bg-indigo-50/50 p-6">
          <p className="text-sm font-black uppercase tracking-widest text-indigo-900">
            {bookingForm.isBlockedSeat ? "Permanent Block" : "Permanent Assignment"}
          </p>
          <p className="mt-2 text-xs font-medium text-indigo-700 leading-relaxed">
            {bookingForm.isBlockedSeat
              ? "This seat will be blocked indefinitely and unavailable for all users."
              : "This seat will be dedicated to the selected person with no expiration. It will be unavailable for all others indefinitely."}
          </p>
          {hasPermanentBooking && (
            <p className="mt-3 text-xs font-semibold text-rose-500">
              This seat already has a permanent assignment.
            </p>
          )}
          {hasPreviousBookings && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                Delete previous bookings for this seat to continue booking
              </p>
            </div>
          )}
          {isUserPermanentlyBooked && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                User already has a permanent assignment in another section.
              </p>
            </div>
          )}
          {userBookedDates.size > 0 && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/90 p-4">
              <p className="text-xs font-bold text-rose-800">
                User already has a booking for the selected date(s).
              </p>
            </div>
          )}
          <div className="mt-4 rounded-2xl border border-indigo-100 bg-white/80 p-4">
            <p className="text-xs font-medium text-indigo-700">
              Admin date selections are limited to 60 days from today. Last selectable date: {formatAdminDisplayDate(getLocalISODate(maxBookingDate))}
            </p>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitDisabled}
        className={`mt-8 w-full rounded-2xl bg-slate-950 px-6 py-5 text-sm font-black uppercase tracking-widest text-white shadow-xl shadow-slate-950/20 transition-all ${
          isSubmitDisabled
            ? "opacity-40 cursor-not-allowed"
            : "hover:scale-[1.02] active:scale-[0.98]"
        }`}
      >
        {bookingForm.isBlockedSeat
          ? bookingForm.bookingMode === "permanent"
            ? "Block Permanently"
            : bookingForm.bookingMode === "specific" && bookingForm.dates.length > 1
            ? `Block for ${bookingForm.dates.length} Dates`
            : previewDateCount !== null && previewDateCount > 1
            ? `Block for ${previewDateCount} Dates`
            : "Block Seat"
          : bookingForm.bookingMode === "permanent"
          ? "Assign Permanently"
          : bookingForm.bookingMode === "specific" && bookingForm.dates.length > 1
          ? `Confirm ${bookingForm.dates.length} Bookings`
          : previewDateCount !== null && previewDateCount > 1
          ? `Confirm ${previewDateCount} Bookings`
          : "Reserve Seat"}
      </button>
    </form>
  );
};

export default BookingForm;
